import React, { useEffect, useRef, useState } from "react";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Divider from "@material-ui/core/Divider";
import { makeStyles } from "@material-ui/core/styles";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { formatTaskDate, mergeHistoryNewestFirst } from "../../pages/Tasks/taskUtils";
import { describeHistoryEvent } from "./taskHistoryUtils";

const useStyles = makeStyles(theme => ({
  event: {
    padding: theme.spacing(1, 0)
  },
  centered: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(2)
  }
}));

const describeSummary = event => {
  const { key, params } = describeHistoryEvent(event);

  if (key === "tasks.history.events.updated" && Array.isArray(params.fieldKeys)) {
    const fieldNames = params.fieldKeys.map(fieldKey => i18n.t(fieldKey)).join(", ");
    return i18n.t("tasks.history.events.updated", { fields: fieldNames });
  }

  return i18n.t(key);
};

// Aba de histórico — própria página/hasMore/loading/sequência de
// requisição, independente da aba de comentários. Reseta e recarrega
// sempre que `taskId` ou `refreshToken` mudam (refreshToken é incrementado
// pelo orquestrador após qualquer ação bem-sucedida).
const TaskHistoryTab = ({ taskId, refreshToken }) => {
  const classes = useStyles();
  const [history, setHistory] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const isMountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchPage = (targetPage, isLoadMore) => {
    const currentRequestId = ++requestIdRef.current;
    if (isLoadMore) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }

    (async () => {
      try {
        const { data } = await api.get(`/tasks/${taskId}/history`, {
          params: { pageNumber: targetPage }
        });

        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;

        setHistory(prev => mergeHistoryNewestFirst(isLoadMore ? prev : [], data.histories));
        setHasMore(data.hasMore);
      } catch (err) {
        if (!isMountedRef.current || currentRequestId !== requestIdRef.current) return;
        toastError(err);
      } finally {
        if (isMountedRef.current && currentRequestId === requestIdRef.current) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    })();
  };

  useEffect(() => {
    // Troca de tarefa/aba ou refresh após ação: invalida requisições
    // antigas, zera tudo e volta para a página 1 antes de consultar de novo.
    requestIdRef.current += 1;
    setHistory([]);
    setHasMore(false);
    setPageNumber(1);
    fetchPage(1, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, refreshToken]);

  const handleLoadMore = () => {
    if (loading || loadingMore || !hasMore) return;
    const nextPage = pageNumber + 1;
    setPageNumber(nextPage);
    fetchPage(nextPage, true);
  };

  if (loading) {
    return (
      <Box className={classes.centered}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (history.length === 0) {
    return (
      <Box className={classes.centered}>
        <Typography color="textSecondary">{i18n.t("tasks.history.empty")}</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {history.map((event, index) => (
        <React.Fragment key={event.id}>
          <Box className={classes.event}>
            <Typography variant="body2">{describeSummary(event)}</Typography>
            <Typography variant="caption" color="textSecondary">
              {event.userName || i18n.t("tasks.table.none")} —{" "}
              {formatTaskDate(event.createdAt, "DD/MM/YYYY HH:mm") || i18n.t("tasks.details.noDate")}
            </Typography>
          </Box>
          {index < history.length - 1 && <Divider />}
        </React.Fragment>
      ))}

      {hasMore && (
        <Box className={classes.centered}>
          <Button onClick={handleLoadMore} disabled={loadingMore} size="small">
            {loadingMore ? <CircularProgress size={18} /> : i18n.t("tasks.history.loadMore")}
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default TaskHistoryTab;
